import { Suspense, lazy, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Box,
  Boxes,
  ChevronDown,
  Columns3,
  MonitorX,
  Network,
  Pencil,
  Printer,
  ScanLine,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { Button, Card, Dialog, EmptyState, Input, Skeleton, Spinner, cn, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { EditWarehouseDialog } from './EditWarehouseDialog.js';
import { useScannerClaim } from '../../../shared/scanner/scanner-claim.js';
import { Segmented, type SegmentedOption } from '../../../shared/ui/Segmented.js';
import { Select } from '../../../shared/ui/Select.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { stockKeys, useStockByCell } from '../../stock/model/stock.queries.js';
import { MoveStockDialog } from '../../stock/ui/MoveStockDialog.js';
import { BatchHistoryDialog } from '../../stock/ui/BatchHistoryDialog.js';
import { StockSearchPage } from '../../stock/ui/StockSearchPage.js';
import { SceneBoundary } from '../../warehouse-3d/ui/SceneBoundary.js';
import { warehouseApi } from '../api/warehouse.api.js';
import { useWarehouseLayout, warehouseKeys } from '../model/warehouse.queries.js';
import { byRank } from '@eombor/contracts';
import type { StockPlaceDto, UpdateCellInput } from '@eombor/contracts';
import { CELL_FILTERS, cellFilterLabel, type CellFilter } from '../model/cell-status.js';
import { LayoutTree, findLayoutCell, flattenLayout, rackTitle } from './LayoutTree.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { applyLevel, applySplit, toCm, type SplitPart } from '../model/split-bay.js';
import { CellAllocateDialog } from './CellAllocateDialog.js';
import { LevelSplitDialog } from './LevelSplitDialog.js';
import { CellPanel } from './CellPanel.js';
import { LabelPrintDialog } from './LabelPrintDialog.js';
import { RackFacade } from './RackFacade.js';
import { SceneTabs } from './SceneTabs.js';
import type { LayoutRack } from '../api/warehouse.api.js';

/**
 * Katakning qatordagi O'RNI (0 dan) — `bays` ro'yxatidagi indeksi.
 *
 * Kodidagi raqam EMAS: birlashtirilgan qatorda raqam tushib qoladi
 * (`01`, `03`, `04`), proyom esa chapdan sanaladi.
 */
const bayIndexOf = (rack: LayoutRack, cell: { id: string; level: number }): number =>
  byRank(rack.cells.filter((item) => item.level === cell.level)).findIndex(
    (item) => item.id === cell.id,
  );

/**
 * 3D sahna **alohida chunk** bilan yuklanadi: `three` va `drei` ~900 kB
 * tashkil qiladi va ular faqat shu sahifada kerak. Kirish sahifasi yoki
 * talabnomalar bo'limi ochayotgan odam ularni umuman yuklab olmaydi — ombor
 * ichidagi internet ko'pincha sekin.
 */
const WarehouseScene = lazy(async () => ({
  default: (await import('../../warehouse-3d/ui/WarehouseScene.js')).WarehouseScene,
}));

/**
 * Ombor kartochkasining ikki ko'rinishi: KATAK qayerda va qanday (`structure`)
 * hamda katakda nima yotibdi (`stock`). Ikkalasi bir bo'limda, lekin ikki
 * ALOHIDA ruxsat ostida — bo'limni birlashtirish ruxsatni birlashtirmaydi.
 */
export type WarehouseView = 'structure' | 'stock';

export interface WarehouseDetailPageProps {
  warehouseId: string;
  /** Qaysi ko'rinish ochiq. Manzildan keladi, shuning uchun havola beriladi. */
  view?: WarehouseView;
  /** Ko'rinishni almashtirish — sahifa marshrutlashni o'zi bilmaydi. */
  onChangeView?: (view: WarehouseView) => void;
  onBack: () => void;
  /**
   * Konstruktorga o'tish — sahifa marshrutlashni o'zi bilmaydi. Berilmasa
   * "Konstruktor" tugmasi umuman chizilmaydi (ruxsatdek: ishlamaydigan tugma
   * ko'rsatilmaydi).
   */
  onOpenBuilder?: (warehouseId: string) => void;
  /** Kirim hujjati ekraniga o'tish — "Mahsulotlar" ko'rinishidan. */
  onNewReceipt?: () => void;
  /**
   * Ochilishda TANLANGAN katak. Skaner kartochkasidagi "Chizmada ko'rsatish"
   * shu bilan keladi: odam katakni chizmada, qo'shni javonlar orasida ko'rishi
   * kerak. Boshlang'ich qiymat — keyin tanlov sahifaning o'ziga o'tadi.
   */
  initialCellId?: string | null;
}

/**
 * Ombor tafsiloti — skladchining asosiy ekrani.
 *
 * Uch qism: chapda tuzilma ro'yxati, o'rtada 3D sahna, o'ngda tanlangan
 * katak kartochkasi. Fokus **doim** skaner maydonida turadi: skladchi
 * javon oldida qo'lida skaner bilan turadi va sichqonchaga tegmasdan kunda
 * yuzlab katakni ochadi. Har muvaffaqiyatli o'qishdan keyin ham fokus shu
 * yerga qaytadi.
 */
export const WarehouseDetailPage = ({
  warehouseId,
  view = 'structure',
  onChangeView,
  onBack,
  onOpenBuilder,
  onNewReceipt,
  initialCellId = null,
}: WarehouseDetailPageProps) => {
  const { can } = usePermission();
  const toast = useToast();
  const scenePanelId = useId();
  const treePanelId = useId();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const reduceMotion = useReducedMotion() === true;
  /* Tor ekranda daraxt yig'ilgan holda boshlanadi: chizma birinchi ekranda ko'rinsin. */
  const [treeOpen, setTreeOpen] = useState(false);
  /* Sahifaning O'Z skaner maydoni bor — u katakni chizmada tanlaydi. Umumiy
     skaner esa alohida sahifa ochardi va odam chizmadan uchib ketardi. */
  useScannerClaim();
  const scannerRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState('');
  /* Qidiruv xatosi maydon OSTIDA turadi, toast'da emas: skladchi kodni
     qayta teradi va xato aynan qayerda ekani ko'z oldida bo'lishi kerak. */
  const [lookupError, setLookupError] = useState<string | null>(null);
  /** Qidiruvda topilgan katak — fasad uni ko'rinishga surib, halqa bilan belgilaydi. */
  const [foundCellId, setFoundCellId] = useState<string | null>(null);
  const [filter, setFilter] = useState<CellFilter>('all');
  const [selectedCellId, setSelectedCellId] = useState<string | null>(initialCellId);
  const [printOpen, setPrintOpen] = useState(false);
  /**
   * Fasadda TANLANGAN kataklar. `null` — tanlov yo'q, ya'ni sarlavhadagi
   * tugma bosilgan va ko'rinib turgan hamma katak ketadi.
   */
  const [printSelection, setPrintSelection] = useState<string[] | null>(null);
  /* Fasad STANDART ko'rinish: kundalik savol "shu javonning qaysi katagi
     bo'sh?" va unga yassi chizma to'g'ridan-to'g'ri javob beradi. 3D kerak
     bo'lganda ochiladi va faqat o'shanda yuklanadi (~900 kB `three`). */
  const [scene, setScene] = useState<'facade' | '3d'>('facade');
  const [facadeRackId, setFacadeRackId] = useState<string | null>(null);
  const [historyBatch, setHistoryBatch] = useState<{ id: string; batchNo: string } | null>(null);
  /* Ko'chirilayotgan partiya. Chizmadagi katak kartochkasidan ochiladi:
     mahsulot noto'g'ri joyda turgani aynan chizmaga qarab bilinadi va
     tuzatish uchun boshqa ekranga o'tish kerak bo'lmasligi kerak. */
  const [movingPlace, setMovingPlace] = useState<StockPlaceDto | null>(null);

  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const layout = useWarehouseLayout(warehouseId);

  /* Katak ichidagi partiyalar faqat katak tanlanganda so'raladi — mingta
     katakning qoldig'i sahifa ochilishida yuklanmaydi. */
  const places = useStockByCell(selectedCellId);

  /* Skaner maydoni faqat tuzilma ko'rinishida bor — `view` ham bog'liqlikda:
     "Mahsulotlar" dan qaytgan skladchi darhol katak kodini kirita olsin. */
  useEffect(() => {
    scannerRef.current?.focus();
  }, [warehouseId, view]);

  const selected = useMemo(
    () => (layout.data === undefined ? null : findLayoutCell(layout.data, selectedCellId)),
    [layout.data, selectedCellId],
  );

  /*
   * Sig'im va izoh `layout` javobida YO'Q: ular mingta katakning har biri
   * uchun tashilsa, sahifa ochilishi behuda og'irlashardi. Tanlangan katak
   * uchun esa alohida so'rov arzon va u SKANER YO'LI bilan bir xil
   * (`by-code` — bitta indeksli qidiruv).
   */
  const cellDetail = useQuery({
    queryKey: ['warehouse', 'cell', warehouseId, selected?.cell.code ?? null],
    queryFn: () => warehouseApi.cellByCode(warehouseId, selected?.cell.code ?? ''),
    enabled: selected !== null,
    staleTime: 60_000,
  });

  const saveCell = useMutation({
    mutationFn: (patch: UpdateCellInput) =>
      warehouseApi.updateCell(selected?.cell.id ?? '', patch),
    onSuccess: async () => {
      // Katak holati rangni o'zgartiradi — chizma ham yangilanishi kerak.
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      await cellDetail.refetch();
      toast.show(t('warehouse.cellSaved'), 'success');
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('warehouse.cellSaveFailed'),
        'danger',
      );
    },
  });

  /*
   * O'RINNI TAQSIMLASH — "bu o'rinda nima turadi?" degan bitta savol.
   *
   * Javob ikki xil bo'ladi va IKKALASI HAM shu yerdan o'tadi:
   *
   *   BUTUN O'RIN bitta mahsulotga — javon o'zgarmaydi, faqat katak
   *   biriktiriladi (`PATCH /cells/:id`).
   *   O'RIN BO'LINADI — geometriya ham o'zgaradi, keyin har qism o'z
   *   mahsulotiga biriktiriladi va etiketka bosiladi.
   *
   * Ilgari birinchisi katak kartochkasidagi ro'yxatda, ikkinchisi
   * alohida oynada edi — bir xil ishning ikki yo'li, va qaysi biri
   * nima qilishi tushunarsiz edi.
   *
   * 1) Bo'linish stellajning `bays` ro'yxatiga tushadi, ya'ni oddiy
   *    `PATCH /racks/:id`. Server kataklarni qayta quradi: mavjudlari
   *    o'z `id` va kodi bilan qoladi — bosilgan etiketkalar kuchda
   *    qolaveradi.
   * 2) Yangi o'rinlar o'sha qatorda `cell.idx` dan boshlab tartib bilan
   *    yotadi; mahsulot tanlangan qismlar `PATCH /cells/:id` bilan
   *    ajratiladi.
   * 3) Etiketka oynasi O'ZI ochiladi va unda AYNAN shu o'rinlar turadi:
   *    yangi o'rin yasab, unga etiketka bosmaslik — javonda nomsiz joy
   *    qoldirish.
   *
   * Ajratish YIQILSA bo'linish qaytarilmaydi: o'lcham allaqachon
   * javonga yozilgan va uni orqaga qaytarish etiketkalarni buzardi.
   * Buning o'rniga ochiq aytiladi — mahsulotni kartochkadan qo'lda
   * biriktirish mumkin.
   */
  const [allocating, setAllocating] = useState(false);
  const allocate = useMutation({
    mutationFn: async (parts: SplitPart[]) => {
      const rack = selected?.rack;
      const cell = selected?.cell;
      if (rack === undefined || cell === undefined) throw new Error('Katak tanlanmagan');

      /*
       * BO'LINMASA javonga umuman TEGILMAYDI. Bitta qism butun o'rinni
       * egallaydi degani — geometriya o'zgarmayapti, faqat katak
       * mahsulotga biriktirilyapti. Bunday holatda ham `PATCH /racks/:id`
       * yuborish kataklarni bekordan-bekorga qayta qurishga majbur
       * qilardi.
       */
      const only = parts[0];
      if (parts.length === 1 && only !== undefined) {
        await warehouseApi.updateCell(cell.id, {
          reservedNomenclatureIds: only.nomenclatureIds,
        });
        return { cellIds: [], failed: 0, split: false };
      }

      const bays = applySplit(
        rack.bays,
        cell.level,
        bayIndexOf(rack, cell),
        parts.map((part) => part.widthCm),
      );
      const node = await warehouseApi.updateRack(rack.id, { bays });

      const slots = parts.map((part, index) => ({
        part,
        cell: node.cells.find(
          (candidate) => candidate.level === cell.level && candidate.idx === cell.idx + index,
        ),
      }));

      let failed = 0;
      for (const slot of slots) {
        if (slot.cell === undefined) continue;
        try {
          await warehouseApi.updateCell(slot.cell.id, {
            reservedNomenclatureIds: slot.part.nomenclatureIds,
          });
        } catch {
          failed += 1;
        }
      }

      return {
        cellIds: slots.flatMap((slot) => (slot.cell === undefined ? [] : [slot.cell.id])),
        failed,
        split: true,
      };
    },
    onSuccess: async ({ cellIds, failed, split }) => {
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      await cellDetail.refetch();
      setAllocating(false);

      if (!split) {
        toast.show(t('warehouse.allocateDone'), 'success');
        return;
      }

      toast.show(
        failed === 0 ? t('warehouse.splitDone') : t('warehouse.splitReserveFailed'),
        failed === 0 ? 'success' : 'danger',
      );
      // Yangi o'rin yasab, unga etiketka bosmaslik — javonda nomsiz joy
      // qoldirish. Shuning uchun oyna O'ZI ochiladi.
      setPrintSelection(cellIds);
      setPrintOpen(true);
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('warehouse.splitFailed'),
        'danger',
      );
    },
  });

  /*
   * QATORNI O'LCHAMLARGA BO'LISH — javonni mahsulotga TAYYORLASH.
   *
   * Katak kartochkasidagi taqsimlash bitta o'rin va bitta mahsulot
   * haqida. Bu esa boshqa ish va boshqa paytda qilinadi: javon hali
   * bo'shligida o'lchab chiqiladi, mahsulot keyin biriktiriladi.
   *
   * Xato OYNADA qoladi: eng ko'p uchraydigani 409 — o'chib ketadigan
   * katakda mahsulot bor. Uni faqat "toast" bilan aytish o'lchamlarni
   * qaytadan terishga majbur qilardi.
   */
  /**
   * KATAKLARNI BIRLASHTIRISH — javondagi ajratgich olib tashlandi.
   *
   * Xato TOAST bilan aytiladi, oynada emas: bu yerda qayta teriladigan
   * forma yo'q — odam tanlovni o'zgartiradi, xolos.
   */
  const mergeCells = useMutation({
    mutationFn: (cellIds: string[]) => warehouseApi.mergeCells(cellIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      toast.show(t('warehouse.mergeDone'), 'success');
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('warehouse.mergeFailed'),
        'danger',
      );
    },
  });

  const [splitLevel, setSplitLevel] = useState<number | null>(null);
  const splitRow = useMutation({
    mutationFn: async (partsCm: number[]) => {
      const rack = facadeRack;
      if (rack === null || splitLevel === null) throw new Error('Qator tanlanmagan');
      const bays = applyLevel(rack.bays, splitLevel, partsCm);
      const node = await warehouseApi.updateRack(rack.id, { bays });
      return node.cells.filter((cell) => cell.level === splitLevel).map((cell) => cell.id);
    },
    onSuccess: async (cellIds) => {
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      setSplitLevel(null);
      splitRow.reset();
      toast.show(t('warehouse.levelSplitDone'), 'success');
      setPrintSelection(cellIds);
      setPrintOpen(true);
    },
  });

  /**
   * Fasadda ko'rsatiladigan stellaj: tanlangan katakniki, aks holda qo'lda
   * tanlangani, aks holda ombordagi birinchisi. Katak tanlanganda chizma
   * O'ZI o'sha javonga o'tadi — skaner bilan kod o'qigan odam qo'shimcha
   * bosishsiz to'g'ri javonni ko'rishi kerak.
   */
  const allRacks = useMemo<LayoutRack[]>(
    () => layout.data?.zones.flatMap((zone) => zone.racks) ?? [],
    [layout.data],
  );

  const facadeRack = useMemo<LayoutRack | null>(() => {
    if (selected !== null) return selected.rack;
    return allRacks.find((rack) => rack.id === facadeRackId) ?? allRacks[0] ?? null;
  }, [selected, allRacks, facadeRackId]);

  /**
   * Etiketka **ko'rinib turgan** kataklar uchun bosiladi: filtr qo'yilgan
   * bo'lsa aynan shu to'plam ketadi. Shu tufayli "faqat yangi bo'sh
   * kataklarga etiketka" ish yo'li qo'shimcha boshqaruvsiz ochiladi.
   */
  const printCellIds = useMemo(
    () => (layout.data === undefined ? [] : flattenLayout(layout.data, filter).map((row) => row.id)),
    [layout.data, filter],
  );

  const lookup = useMutation({
    mutationFn: (input: string) => warehouseApi.cellByCode(warehouseId, input),
    onSuccess: (cell) => {
      setLookupError(null);
      setFoundCellId(cell.id);
      setSelectedCellId(cell.id);
      /* Chizma topilgan katakning javonida QOLADI: kartochka yopilgach
         ham skladchi uni qo'shnilari orasida ko'rishi kerak, birinchi
         javonga qaytib ketmasligi. */
      const found = layout.data === undefined ? null : findLayoutCell(layout.data, cell.id);
      if (found !== null) setFacadeRackId(found.rack.id);
      setCode('');
      scannerRef.current?.focus();
    },
    onError: (error: unknown) => {
      setFoundCellId(null);
      setLookupError(
        error instanceof ApiClientError && error.status === 404
          ? t('warehouse.scanNotFound')
          : error instanceof ApiClientError
            ? error.message
            : t('warehouse.scanFailed'),
      );
      scannerRef.current?.select();
    },
  });

  const warehouse = layout.data?.warehouse;

  /* Ruxsat aynan shu ombor uchun so'raladi: MTU ga berilgan grant boshqa
     MTU ning omborida tugma chizmasligi kerak. Qaror baribir backendda. */
  const target = {
    warehouseId,
    ...(warehouse === undefined ? {} : { mtuId: warehouse.mtuId, orgUnitId: warehouse.orgUnitId }),
  };
  const canPrint = can('warehouse.label.print', target);
  const canEditLayout = can('warehouse.layout.edit', target);
  const canMove = can('stock.move', target);

  /*
   * Katalog FAQAT tuzilmani tahrirlovchiga kerak: katakni mahsulot uchun
   * ajratish shu ruxsat ostida. Oddiy skladchi sahifani ochganda ortiqcha
   * so'rov ketmasin — ombor ichidagi internet ko'pincha sekin.
   */
  const reservable = useNomenclatures(canEditLayout ? { limit: 200 } : {});

  /* MUHIM: bir bo'lim, ikki RUXSAT. `warehouse.read` katakning qayerdaligini,
     `stock.read` esa ichida nima yotganini ochadi — ular birlashmaydi.
     Ruxsati yo'q ko'rinishning segmenti umuman chizilmaydi, manzil bilan
     to'g'ridan-to'g'ri kirilganda esa sabab yozilgan kartochka chiqadi. */
  const canReadLayout = can('warehouse.read', target);
  const canReadStock = can('stock.read', target);

  const viewOptions: SegmentedOption<WarehouseView>[] = [
    ...(canReadLayout
      ? [
          {
            value: 'structure' as const,
            label: t('warehouse.viewStructure'),
            icon: <Network size={14} aria-hidden />,
          },
        ]
      : []),
    ...(canReadStock
      ? [
          {
            value: 'stock' as const,
            label: t('warehouse.viewStock'),
            icon: <Boxes size={14} aria-hidden />,
          },
        ]
      : []),
  ];

  /* WebGL BOR-YO'QLIGI oldindan tekshiriladi: sahnani yuklab, keyin
     yiqilishini kutish ~900 kB ni bekorga tortib olardi. Faqat 3D tab
     ochilganda — fasadda kanvas umuman yaratilmaydi. */
  const webglSupported = useMemo(() => scene !== '3d' || hasWebGL(), [scene]);

  const structure = view === 'structure';
  /* Faqat TUZILMA ko'rinishi va keng ekranda sahifa viewport'ga sig'adi:
     panellar o'z ichida aylanadi, hujjat esa ortiqcha bo'sh joyga
     cho'zilmaydi. 2.5rem — `main` ning yuqori va pastki `padding` i. */
  const fitViewport = structure && desktop && !layout.isPending && !layout.isError;

  const treeCount =
    layout.data === undefined ? 0 : flattenLayout(layout.data, filter).length;

  const tree =
    layout.data === undefined ? null : (
      <LayoutTree
        layout={layout.data}
        filter={filter}
        selectedCellId={selectedCellId}
        onSelectCell={setSelectedCellId}
      />
    );

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-4',
        fitViewport ? 'h-[calc(100dvh-var(--size-topbar)-2.5rem)]' : undefined,
      )}
    >
      <header className="flex shrink-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-col gap-2">
          <button
            type="button"
            onClick={onBack}
            className={cn(
              'group inline-flex h-[var(--size-control)] items-center gap-2 self-start rounded-xl',
              'border border-border bg-surface px-3.5 text-sm font-medium text-text',
              'shadow-[var(--shadow-1)] outline-none',
              'transition-[background-color,border-color,box-shadow,scale] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)] hover:border-border-strong hover:bg-surface-2',
              'hover:shadow-[var(--shadow-2)] focus-visible:ring-2 focus-visible:ring-accent active:scale-[0.97]',
            )}
          >
            <ArrowLeft
              size={16}
              aria-hidden
              className="transition-transform duration-[var(--motion-base)] ease-[var(--ease-out-quick)] motion-safe:group-hover:-translate-x-[3px]"
            />
            {t('warehouse.backToList')}
          </button>

          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="min-w-0 text-2xl leading-8 font-semibold tracking-tight text-balance text-text">
              {warehouse?.name ?? t('warehouse.detailTitle')}
            </h1>
            {warehouse?.code === undefined ? null : (
              <span className="tabular inline-flex h-6 items-center rounded-md border border-border bg-surface-2 px-2 font-mono text-xs font-medium text-text-muted">
                {warehouse.code}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Segment yuklanish tugagach chiziladi: ruxsat MTU doirasida
                berilgan bo'lsa, ombor qaysi MTU ga tegishli ekani `layout`
                javobi bilan keladi — undan oldin javob "yo'q" bo'lardi. */}
            {layout.isPending || viewOptions.length < 2 || onChangeView === undefined ? null : (
              <Segmented
                label={t('warehouse.viewLabel')}
                value={view}
                options={viewOptions}
                onChange={onChangeView}
              />
            )}

            {/* Tahrirlash — nom, manzil, o'lcham va faollik. Ruxsat
                konstruktor bilan bir xil (`warehouse.layout.edit`), chunki
                backend `PATCH /warehouses/:id` ni aynan shu bilan
                tekshiradi. */}
            {structure && canEditLayout && warehouse !== undefined ? (
              <Button variant="secondary" size="sm" className={LIFT} onClick={() => setEditOpen(true)}>
                <Pencil size={14} aria-hidden />
                {t('warehouse.edit')}
              </Button>
            ) : null}

            {structure && canEditLayout && onOpenBuilder !== undefined ? (
              <Button
                variant="secondary"
                size="sm"
                className={LIFT}
                onClick={() => onOpenBuilder(warehouseId)}
              >
                <SlidersHorizontal size={14} aria-hidden />
                {t('warehouse.openBuilder')}
              </Button>
            ) : null}

            {structure && canPrint ? (
              <Button
                variant="secondary"
                size="sm"
                className={LIFT}
                onClick={() => {
                  // Sarlavhadagi tugma DOIM ko'rinib turgan hamma katakni
                  // oladi — fasaddagi eski tanlov unga qo'shilib ketmasin.
                  setPrintSelection(null);
                  setPrintOpen(true);
                }}
              >
                <Printer size={14} aria-hidden />
                {t('warehouse.printLabels')}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Skaner va filtr — TUZILMA ko'rinishining boshqaruvlari.
            "Mahsulotlar" da ular chizilmaydi (yashirilmaydi: yashirin
            `autoFocus` maydon fokusni o'g'irlab qolardi).

            Tor ekranda: kod + tugma bir qatorda, holat ostida to'liq
            kenglikda. Kengida — hammasi bir qatorda, sarlavha qatoriga
            tekislangan (yorliqlar bir tekis). Tepadan tekislanadi, pastdan
            emas: inline xato chiqqanda blok PASTGA o'sadi, maydon esa
            joyidan qimirlamaydi. */}
        {structure ? (
          <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-3 sm:grid-cols-[minmax(0,1fr)_auto_12rem] lg:w-auto lg:grid-cols-[14rem_auto_11rem] lg:pt-[calc(var(--size-control)+0.5rem)]">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const trimmed = code.trim().toUpperCase();
                if (trimmed !== '' && !lookup.isPending) lookup.mutate(trimmed);
              }}
              className="contents"
            >
              <Input
                ref={scannerRef}
                mono
                autoFocus
                label={t('warehouse.scanLabel')}
                placeholder="A-03-02-05"
                prefix={<ScanLine size={16} aria-hidden />}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  if (lookupError !== null) setLookupError(null);
                }}
                {...(lookupError === null ? {} : { error: lookupError })}
                className="min-w-0"
              />
              {/* Yorliq balandligicha bo'shliq — tugma maydon bilan bir
                  chiziqda turadi, xato matni chiqqanda ham siljimaydi. */}
              <div className="flex flex-col gap-1.5">
                <span aria-hidden className="text-sm leading-5 select-none">
                  &nbsp;
                </span>
                <SearchButton disabled={code.trim() === ''} pending={lookup.isPending} />
              </div>
            </form>

            <Select
              label={t('warehouse.filterLabel')}
              value={filter}
              options={CELL_FILTERS.map((value) => ({ value, label: cellFilterLabel(value) }))}
              onChange={(event) => setFilter(event.target.value as CellFilter)}
              className="col-span-2 sm:col-span-1"
            />
          </div>
        ) : null}
      </header>

      {layout.isPending ? (
        <Skeleton className="h-[32rem] w-full" />
      ) : view === 'stock' ? (
        canReadStock ? (
          <StockSearchPage
            warehouseId={warehouseId}
            {...(onNewReceipt === undefined ? {} : { onNewReceipt })}
          />
        ) : (
          <Card>
            <EmptyState
              icon={<Boxes size={20} aria-hidden />}
              title={t('warehouse.stockForbidden')}
              description={t('warehouse.stockForbiddenHint')}
            />
          </Card>
        )
      ) : layout.isError ? (
        <Card>
          <EmptyState
            title={t('warehouse.loadError')}
            description={layout.error instanceof ApiClientError ? layout.error.message : undefined}
            action={
              <Button variant="secondary" onClick={() => void layout.refetch()}>
                {t('warehouse.retry')}
              </Button>
            }
          />
        </Card>
      ) : (
        /* `items-start` — o'ng karta chap panel balandligiga CHO'ZILMAYDI.
           Ikkala panel ham `max-h-full`: kontenti sig'masa o'z ichida
           aylanadi, sahifa esa viewport'dan oshmaydi. */
        <div
          className={cn(
            'grid min-w-0 items-start gap-4',
            'lg:min-h-0 lg:flex-1 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]',
            'xl:grid-cols-[20rem_minmax(0,1fr)]',
          )}
        >
          {desktop ? (
            <Card className="min-w-0 lg:max-h-full" padded={false} bodyClassName="flex min-h-0 flex-col">
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3">
                <h2 className="text-sm font-semibold text-text">{t('warehouse.treeTitle')}</h2>
                <span className="tabular rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-text-muted ring-1 ring-border">
                  {treeCount}
                </span>
              </div>
              {/*
                `relative` SHART. Daraxtda yuzlab `sr-only` yorliq bor va
                ular `position: absolute`. Konteyner joylashtirilmagan
                (`static`) bo'lsa, ular `overflow` dan QOCHIB hujjatga
                bog'lanadi va sahifani bo'sh joyga cho'zadi — aynan shu
                «scroll qilinganda oq fon tugab qoladi» xatosi edi
                (1440×900 da hujjat 1916px bo'lib qolardi).
              */}
              <div
                className={cn(
                  'relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pt-2 pb-4',
                  '[scrollbar-width:thin] [scrollbar-gutter:stable]',
                  '[mask-image:linear-gradient(to_bottom,transparent,#000_10px,#000_calc(100%-18px),transparent)]',
                )}
              >
                {tree}
              </div>
            </Card>
          ) : (
            <Card className="min-w-0" padded={false}>
              <button
                type="button"
                aria-expanded={treeOpen}
                aria-controls={treePanelId}
                onClick={() => setTreeOpen((open) => !open)}
                className="flex min-h-[var(--size-control)] w-full items-center gap-2 px-4 py-2 text-left outline-none hover:bg-hover focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
              >
                <ChevronDown
                  size={18}
                  aria-hidden
                  className={cn(
                    'shrink-0 text-text-muted transition-transform duration-[var(--motion-base)]',
                    treeOpen ? 'rotate-180' : undefined,
                  )}
                />
                <span className="flex-1 text-sm font-semibold text-text">{t('warehouse.treeTitle')}</span>
                <span className="tabular rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-text-muted ring-1 ring-border">
                  {treeCount}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {treeOpen ? (
                  <motion.div
                    id={treePanelId}
                    key="tree"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden border-t border-border"
                  >
                    <div className="relative max-h-[50dvh] overflow-y-auto overscroll-contain px-2 pt-2 pb-4 [scrollbar-width:thin]">
                      {tree}
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </Card>
          )}

          <Card className="min-w-0 lg:max-h-full" padded={false} bodyClassName="flex min-h-0 flex-col">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-3 py-2.5">
              <SceneTabs
                label={t('warehouse.sceneLabel')}
                value={scene}
                tabs={[
                  { value: 'facade' as const, label: t('warehouse.sceneFacade'), icon: <Columns3 size={14} aria-hidden /> },
                  { value: '3d' as const, label: t('warehouse.scene3d'), icon: <Box size={14} aria-hidden /> },
                ]}
                onChange={setScene}
                panelId={scenePanelId}
                idOf={(value) => `${scenePanelId}-${value}`}
              />
            </div>

            <div
              id={scenePanelId}
              role="tabpanel"
              aria-labelledby={`${scenePanelId}-${scene}`}
              className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-width:thin]"
            >
              {scene === 'facade' ? (
                facadeRack === null ? (
                  <div className="p-4">
                    <EmptyState
                      title={t('warehouse.facadeEmpty')}
                      description={t('warehouse.facadeEmptyHint')}
                    />
                  </div>
                ) : (
                  <div className="p-4">
                    <RackFacade
                      rack={facadeRack}
                      racks={allRacks}
                      onSelectRack={(rackId) => {
                        setFacadeRackId(rackId);
                        // Tanlangan katak boshqa javondan bo'lsa, chizma bilan
                        // panel bir-biriga zid bo'lib qolardi.
                        setSelectedCellId(null);
                        setFoundCellId(null);
                      }}
                      filter={filter}
                      selectedCellId={selectedCellId}
                      foundCellId={foundCellId}
                      onSelectCell={(cellId) => {
                        setFacadeRackId(facadeRack.id);
                        setSelectedCellId(cellId);
                      }}
                      {...(canPrint
                        ? {
                            onPrint: (cellIds: string[]) => {
                              setPrintSelection(cellIds);
                              setPrintOpen(true);
                            },
                          }
                        : {})}
                      {...(canEditLayout
                        ? { onSplitLevel: setSplitLevel, onMerge: mergeCells.mutate }
                        : {})}
                    />
                  </div>
                )
              ) : webglSupported ? (
                /* Sahna yiqilsa (WebGL kontekst yaratilmadi) sahifa ishlashda
                   davom etadi — ma'lumot chapdagi ro'yxatda, fasadda va
                   sahnaning `sr-only` jadvalida.

                   Kanvas FAQAT shu tab faol bo'lganda chiziladi: tab
                   almashganda u o'chiriladi va R3F renderer, geometriya va
                   WebGL kontekstini o'zi bo'shatadi — fonda kontekst
                   qolib, xotira sizib ketmaydi. */
                <SceneBoundary fallback={<SceneUnavailable />}>
                  <Suspense
                    fallback={
                      <div className="flex h-[60dvh] flex-col items-center justify-center gap-3 lg:h-[36rem]">
                        <Skeleton className="absolute inset-0 rounded-none" />
                        <span className="relative flex items-center gap-2 text-sm font-medium text-text-muted">
                          <Spinner size="sm" />
                          {t('warehouse.scene3dLoading')}
                        </span>
                      </div>
                    }
                  >
                    <WarehouseScene
                      layout={layout.data}
                      selectedCellId={selectedCellId}
                      onSelectCell={setSelectedCellId}
                      viewportClassName="h-[60dvh] lg:h-[36rem]"
                    />
                  </Suspense>
                </SceneBoundary>
              ) : (
                <SceneUnavailable />
              )}
            </div>
          </Card>

          {/*
            KATAK KARTOCHKASI — MODAL.

            Ilgari u yon panel edi va chizmani siqib qo'yardi: 80 metrli
            ombor rejasi yarim ekranga tushib, katakni tanlash uchun
            avval uni topish kerak bo'lardi. Modal chizmani joyida
            qoldiradi va yopilgach ko'z o'sha joyga qaytadi.
          */}
          <Dialog
            open={selected !== null}
            onClose={() => {
              setSelectedCellId(null);
              scannerRef.current?.focus();
            }}
            title={selected === null ? '' : selected.cell.code}
            size="lg"
          >
            {selected === null ? null : (
              <div className="min-w-0">
                <CellPanel
                  variant="dialog"
                  cell={selected.cell}
                  rackName={rackTitle(selected.rack)}
                  capacityQty={cellDetail.data?.capacityQty ?? null}
                  capacityWeightKg={cellDetail.data?.capacityWeightKg ?? null}
                  note={cellDetail.data?.note ?? null}
                  widthCm={
                    selected.rack.bays[selected.cell.level - 1]?.[
                      bayIndexOf(selected.rack, selected.cell)
                    ] === undefined
                      ? null
                      : toCm(
                          selected.rack.bays[selected.cell.level - 1]?.[
                            bayIndexOf(selected.rack, selected.cell)
                          ] as number,
                        )
                  }
                  {...(canEditLayout
                    ? { onSave: saveCell.mutate, onAllocate: () => setAllocating(true) }
                    : {})}
                  savePending={saveCell.isPending}
                  places={places.data ?? []}
                  placesPending={places.isPending}
                  onShowHistory={(place) =>
                    setHistoryBatch({ id: place.batchId, batchNo: place.batchNo })
                  }
                  {...(canMove ? { onMove: (place) => setMovingPlace(place) } : {})}
                  onClose={() => {
                    setSelectedCellId(null);
                    scannerRef.current?.focus();
                  }}
                />
              </div>
            )}
          </Dialog>
        </div>
      )}

      {/* Bo'lish oynasi — katak kartochkasi USTIDA ochiladi va yopilgach
          kartochka joyida qoladi: odam natijani darhol ko'radi. */}
      {selected === null || !canEditLayout ? null : (
        <CellAllocateDialog
          open={allocating}
          onClose={() => setAllocating(false)}
          cellCode={selected.cell.code}
          level={selected.cell.level}
          bayM={
            selected.rack.bays[selected.cell.level - 1]?.[
              bayIndexOf(selected.rack, selected.cell)
            ] ?? 0
          }
          bayNumber={selected.cell.idx}
          reservedIds={selected.cell.reservedFor.map((item) => item.id)}
          products={reservable.data?.items ?? []}
          onSubmit={(parts) => allocate.mutate(parts)}
          pending={allocate.isPending}
        />
      )}

      {/* Qatorni o'lchash oynasi — fasaddagi qator tugmasidan ochiladi. */}
      {facadeRack === null || !canEditLayout ? null : (
        <LevelSplitDialog
          open={splitLevel !== null}
          onClose={() => {
            setSplitLevel(null);
            splitRow.reset();
          }}
          rackCode={facadeRack.code}
          level={splitLevel ?? 1}
          currentM={facadeRack.bays[(splitLevel ?? 1) - 1] ?? []}
          onSubmit={(partsCm) => splitRow.mutate(partsCm)}
          pending={splitRow.isPending}
          error={
            splitRow.error instanceof ApiClientError
              ? splitRow.error.message
              : splitRow.error === null
                ? null
                : t('warehouse.levelSplitFailed')
          }
        />
      )}

      {canPrint && view === 'structure' ? (
        <LabelPrintDialog
          open={printOpen}
          onClose={() => {
            setPrintOpen(false);
            setPrintSelection(null);
          }}
          cellIds={printSelection ?? printCellIds}
        />
      ) : null}

      {/* Ombor hali yuklanmagan bo'lsa oyna umuman chizilmaydi: forma
          boshlang'ich qiymatlarni omborning O'ZIDAN oladi. */}
      {canEditLayout && warehouse !== undefined ? (
        <EditWarehouseDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          warehouse={warehouse}
        />
      ) : null}

      {/* Ko'chirish oynasi katak kartochkasi USTIDA ochiladi va yopilgach
          kartochka joyida qoladi: odam natijani darhol ko'radi. */}
      {selected === null ? null : (
        <MoveStockDialog
          open={movingPlace !== null}
          onClose={() => setMovingPlace(null)}
          warehouseId={warehouseId}
          fromCellId={selected.cell.id}
          fromCellCode={selected.cell.code}
          target={
            movingPlace === null
              ? null
              : {
                  batchId: movingPlace.batchId,
                  batchNo: movingPlace.batchNo,
                  nomenclatureId: movingPlace.nomenclatureId,
                  nomenclatureName: movingPlace.nomenclatureName,
                  unit: movingPlace.unit,
                  qty: movingPlace.qty,
                  availableQty: movingPlace.availableQty,
                }
          }
          onMoved={() => {
            setMovingPlace(null);
            /* Chizma ham yangilanadi: katak endi bo'shagan yoki to'lgan
               bo'lishi mumkin va rangi shundan hisoblanadi. */
            void queryClient.invalidateQueries({ queryKey: stockKeys.all });
            void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
          }}
        />
      )}

      <BatchHistoryDialog
        open={historyBatch !== null}
        onClose={() => setHistoryBatch(null)}
        batchId={historyBatch?.id ?? null}
        batchNo={historyBatch?.batchNo ?? ''}
      />
    </div>
  );
};

/** Tugmalarning «jonli» harakati: hover da ko'tariladi, bosilganda siqiladi. */
const LIFT =
  'transition-[translate,scale,box-shadow,background-color] motion-safe:hover:-translate-y-px hover:shadow-[var(--shadow-2)] active:scale-[0.97]';

/** Brauzer WebGL kontekst bera oladimi — sinov kanvasi darhol tashlanadi. */
const hasWebGL = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    context?.getExtension('WEBGL_lose_context')?.loseContext();
    return context !== null;
  } catch {
    return false;
  }
};

/**
 * «TOPISH» — kod bo'sh bo'lsa ANIQ o'chiq (kulrang, shaffof emas),
 * kod yozilganda asosiy rangga silliq o'tadi. Kutilayotganda spinner:
 * skaner ikki marta o'qisa ham so'rov ikkilanmaydi.
 *
 * Umumiy `Button` ning `disabled` holati — xiralashgan asosiy rang, va
 * u bu yerda «bosiladi, lekin javob bermayapti» deb o'qilardi.
 */
const SearchButton = ({ disabled, pending }: { disabled: boolean; pending: boolean }) => (
  <button
    type="submit"
    disabled={disabled}
    aria-busy={pending}
    className={cn(
      'inline-flex h-[var(--size-control)] items-center justify-center gap-2 rounded-[var(--radius-sm)] px-5',
      'border text-sm font-semibold whitespace-nowrap outline-none select-none',
      'transition-[background-color,border-color,color,box-shadow,translate,scale] duration-[var(--motion-base)]',
      'ease-[var(--ease-standard)] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
      disabled
        ? 'cursor-not-allowed border-border bg-surface-2 text-text-faint'
        : cn('border-primary bg-primary text-primary-contrast shadow-[var(--shadow-1)] hover:bg-primary-300', LIFT),
    )}
  >
    {pending ? <Spinner size="sm" /> : <Search size={16} aria-hidden />}
    {t('warehouse.scanSubmit')}
  </button>
);

/** 3D ochilmadi — sabab va yo'l: ombor baribir to'liq boshqariladi. */
const SceneUnavailable = () => (
  <div className="flex min-h-[20rem] items-center justify-center p-6">
    <EmptyState
      icon={<MonitorX size={22} aria-hidden />}
      title={t('warehouse3d.unsupported')}
      description={t('warehouse3d.unsupportedHint')}
      className="max-w-md rounded-2xl border border-dashed border-border-strong bg-surface-2/50"
    />
  </div>
);
